import { create } from 'zustand';

import type { WalletEntity } from '@/types';

/**
 * Per-(user, group) wallet store(R45 / Beta v0.0.1)
 * -----------------------------------------------------------------------------
 * 對齊 docs/40 §6 + R45 8-agent panel synthesis:
 *
 * - Map<gid, WalletEntity>:同 user 在 N 個 group 各有獨立 wallet(200 點/月配額)
 * - Realtime:Firestore `users/{cuid}/wallets/{gid}` doc onSnapshot push 寫進來;
 *   切 group 不需 detach 舊 group listener(每個 listener 都活著訂自己的 gid)
 * - **不持久化**(每次 boot 重 fetch / 重訂);避免 stale 跨 user / 跨 group leak
 *   (對齊 Security Engineer panel rule「no group-scoped key without prefix」)
 *
 * **groupId guard**(R45 Security Engineer 警示):每次 setter 必 check
 * `wallet.groupId === gid`,防 cross-group race(切 group 期間 stale onSnapshot
 * callback 把舊 group wallet 寫進新 group bucket — entry 物理上鎖死 by gid)。
 */
export interface WalletState {
  /** Map<gid, WalletEntity>;currentGroup 切換時讀對應 gid 即可。 */
  wallets: Map<string, WalletEntity>;
  /**
   * 寫入 wallet(`gid` 必須跟 `wallet.groupId` 一致,否則 drop)。
   * 對齊 R45 Security Engineer panel「groupId guard in store setters」。
   */
  setWallet: (gid: string, wallet: WalletEntity) => void;
  /** 取指定 group 的 wallet(currentGroup / project.group_id 都會打); */
  getWallet: (gid: string) => WalletEntity | undefined;
  /** 移除指定 group 的 wallet(切 group 後可選擇清掉舊 entry 節省記憶體;optional 動作)。 */
  removeWallet: (gid: string) => void;
  /** Logout 全清。 */
  clear: () => void;
}

export const useWalletStore = create<WalletState>()((set, get) => ({
  wallets: new Map(),
  setWallet: (gid, wallet) => {
    if (wallet.groupId !== gid) {
      // groupId guard:wallet 內部 groupId 跟 entry key 不一致 → 拒寫
      // 對齊 R45 Security Engineer cross-group race 防護
      console.warn(
        `[walletStore] cross-group write blocked: key=${gid} but wallet.groupId=${wallet.groupId}`,
      );
      return;
    }
    set((state) => {
      const next = new Map(state.wallets);
      next.set(gid, wallet);
      return { wallets: next };
    });
  },
  getWallet: (gid) => get().wallets.get(gid),
  removeWallet: (gid) =>
    set((state) => {
      const next = new Map(state.wallets);
      next.delete(gid);
      return { wallets: next };
    }),
  clear: () => set({ wallets: new Map() }),
}));
